/**
 * FormulaEngine - Basic formula parser and evaluator for SheetsApp_v2
 * Supports common spreadsheet functions and operations
 */

import { FormulaError } from './SheetsConfig';
// Removed unused imports - functions implemented locally if needed

/**
 * Formula evaluation result
 */
export interface FormulaResult {
  value: any;
  error?: FormulaError;
  dependencies: string[]; // Cell references this formula depends on
}

/**
 * Cell reference for formula evaluation
 */
export interface CellRef {
  row: number;
  col: number;
  value: any;
}

/**
 * Formula Engine - Parses and evaluates spreadsheet formulas
 */
export class FormulaEngine {
  private cellValues: Map<string, any>; // cellRef (e.g., "A1") -> value

  private static readonly PRECEDENCE: Record<string, number> = {
    '^': 4,
    '*': 3, '/': 3,
    '+': 2, '-': 2,
    '&': 1,
    '>': 0, '<': 0, '>=': 0, '<=': 0, '=': 0, '<>': 0, '!=': 0,
  };

  private static readonly RIGHT_ASSOC = new Set(['^']);

  constructor() {
    this.cellValues = new Map();
  }

  /**
   * Set cell value for formula evaluation
   */
  public setCellValue(cellRef: string, value: any): void {
    this.cellValues.set(cellRef.toUpperCase(), value);
  }

  /**
   * Get cell value
   */
  public getCellValue(cellRef: string): any {
    return this.cellValues.get(cellRef.toUpperCase());
  }

  /**
   * Clear all cell values
   */
  public clear(): void {
    this.cellValues.clear();
  }

  /**
   * Evaluate a formula
   */
  public evaluate(formula: string): FormulaResult {
    try {
      // Remove leading "=" if present
      const cleanFormula = formula.trim().startsWith('=') 
        ? formula.trim().substring(1) 
        : formula.trim();

      const dependencies: string[] = [];
      
      // Extract cell references (A1, B2, etc.) and ranges (A1:B10)
      const cellRefPattern = /\$?[A-Z]+\$?[0-9]+/g;
      const rangePattern = /\$?[A-Z]+\$?[0-9]+:\$?[A-Z]+\$?[0-9]+/g;
      
      // Extract ranges first
      const rangeMatches = cleanFormula.match(rangePattern);
      if (rangeMatches) {
        rangeMatches.forEach(range => {
          // Expand range to individual cells for dependencies
          const [start, end] = range.split(':');
          const startPos = this.parseA1Notation(start);
          const endPos = this.parseA1Notation(end);
          
          for (let r = startPos.row; r <= endPos.row; r++) {
            for (let c = startPos.col; c <= endPos.col; c++) {
              dependencies.push(this.toA1Notation(r, c));
            }
          }
        });
      }
      
      // Extract individual cell references
      const cellMatches = cleanFormula.match(cellRefPattern);
      if (cellMatches) {
        // ✅ FIX #6: Filter out function names (they're followed by '(')
        const filteredRefs = cellMatches.filter(ref => {
          const refIndex = cleanFormula.indexOf(ref);
          const charAfter = cleanFormula[refIndex + ref.length];
          // If followed by '(', it's a function call, not a cell reference
          return charAfter !== '(';
        });
        dependencies.push(...filteredRefs.map(ref => ref.toUpperCase()));
      }

      // Evaluate the formula
      const value = this.evaluateExpression(cleanFormula);

      return {
        value,
        dependencies: Array.from(new Set(dependencies)) // Remove duplicates
      };
    } catch (error) {
      return {
        value: null,
        error: FormulaError.VALUE,
        dependencies: []
      };
    }
  }

  /**
   * Evaluate expression using Shunting-Yard for proper operator precedence
   */
  private evaluateExpression(expr: string): any {
    expr = expr.trim();

    if (expr === '') return '';

    // Check for range (A1:B10)
    if (this.isRange(expr)) {
      return this.expandRange(expr);
    }

    // If expression has function calls, handle them first
    if (/[A-Z]+\(/i.test(expr)) {
      return this.evaluateWithFunctions(expr);
    }

    return this.shuntingYardEvaluate(expr);
  }

  private evaluateWithFunctions(expr: string): any {
    let result = expr;

    let maxIter = 100;
    while (/[A-Z]+\(/i.test(result) && maxIter-- > 0) {
      const match = result.match(/([A-Z]+)\(([^()]*)\)/i);
      if (!match) break;

      const fullMatch = match[0];
      const fnName = match[1].toUpperCase();
      const argsStr = match[2];

      const args = this.parseArgs(argsStr);
      const evaluatedArgs = args.map(arg => {
        const trimmed = arg.trim();
        if (trimmed === '') return '';
        try {
          return this.evaluateExpression(trimmed);
        } catch {
          return trimmed;
        }
      });

      let fnResult: any;
      switch (fnName) {
        case 'SUM':
          fnResult = this.flattenArgs(evaluatedArgs)
            .map(v => Number(v))
            .filter(v => !isNaN(v))
            .reduce((sum, val) => sum + val, 0);
          break;
        case 'AVERAGE':
        case 'AVG': {
          const numbers = this.flattenArgs(evaluatedArgs)
            .map(v => Number(v))
            .filter(v => !isNaN(v));
          fnResult = numbers.length > 0 ? numbers.reduce((s, v) => s + v, 0) / numbers.length : 0;
          break;
        }
        default:
          fnResult = this.evaluateFunction(fullMatch);
          break;
      }

      // Use match.index (position of the innermost match) to avoid replacing the
      // wrong occurrence when the same function appears multiple times.
      const idx = match.index!;
      const before = result.substring(0, idx);
      const after = result.substring(idx + fullMatch.length);

      const resultStr = typeof fnResult === 'string' && !(/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(fnResult))
        ? `"${fnResult}"`
        : String(fnResult);

      result = before + resultStr + after;
    }

    if (/[A-Z]+\(/i.test(result)) {
      return this.evaluateFunction(expr);
    }

    return this.shuntingYardEvaluate(result);
  }

  private tokenize(expr: string): Array<{ type: string; value: string }> {
    const tokens: Array<{ type: string; value: string }> = [];
    let i = 0;

    while (i < expr.length) {
      const ch = expr[i];

      if (ch === ' ' || ch === '\t') { i++; continue; }

      if (ch === '"' || ch === "'") {
        const quote = ch;
        let str = '';
        i++;
        while (i < expr.length && expr[i] !== quote) {
          str += expr[i]; i++;
        }
        i++;
        tokens.push({ type: 'string', value: str });
        continue;
      }

      if (/\d/.test(ch) || (ch === '.' && i + 1 < expr.length && /\d/.test(expr[i + 1]))) {
        let num = '';
        while (i < expr.length && /[\d.eE]/.test(expr[i])) {
          num += expr[i]; i++;
          if ((expr[i - 1] === 'e' || expr[i - 1] === 'E') && (expr[i] === '+' || expr[i] === '-')) {
            num += expr[i]; i++;
          }
        }
        tokens.push({ type: 'number', value: num });
        continue;
      }

      if (i + 1 < expr.length) {
        const two = expr.substring(i, i + 2);
        if (two === '>=' || two === '<=' || two === '<>' || two === '!=') {
          tokens.push({ type: 'operator', value: two });
          i += 2;
          continue;
        }
      }

      if ('+-*/%^&>=<'.includes(ch)) {
        const prev = tokens.length > 0 ? tokens[tokens.length - 1] : null;
        const prevIsOperand = prev && (prev.type === 'number' || prev.type === 'string' || prev.type === 'boolean' || prev.type === 'ref' || prev.type === 'rparen');
        if (ch === '%' && prevIsOperand) {
          // Postfix unary percentage: 5% → 5/100
          tokens.push({ type: 'postfix_percent', value: '%' });
        } else if ((ch === '-' || ch === '+') && !prevIsOperand) {
          tokens.push({ type: 'unary', value: ch });
        } else {
          tokens.push({ type: 'operator', value: ch });
        }
        i++;
        continue;
      }

      if (ch === '(') { tokens.push({ type: 'lparen', value: '(' }); i++; continue; }
      if (ch === ')') { tokens.push({ type: 'rparen', value: ')' }); i++; continue; }

      if (ch === ',') { tokens.push({ type: 'comma', value: ',' }); i++; continue; }

      if (ch === ':') { tokens.push({ type: 'colon', value: ':' }); i++; continue; }

      if (/[A-Za-z$_]/.test(ch)) {
        let ident = '';
        if (ch === '$') {
          ident += ch; i++;
        }
        while (i < expr.length && /[A-Za-z0-9$_]/.test(expr[i])) {
          ident += expr[i]; i++;
        }

        if (i < expr.length && expr[i] === '(') {
          tokens.push({ type: 'function', value: ident.toUpperCase() });
        } else if (ident.toUpperCase() === 'TRUE') {
          tokens.push({ type: 'boolean', value: 'true' });
        } else if (ident.toUpperCase() === 'FALSE') {
          tokens.push({ type: 'boolean', value: 'false' });
        } else {
          tokens.push({ type: 'ref', value: ident.toUpperCase() });
        }
        continue;
      }

      i++;
    }

    return tokens;
  }

  private shuntingYardEvaluate(expr: string): any {
    const tokens = this.tokenize(expr);
    if (tokens.length === 0) return '';

    if (tokens.length === 1) {
      const tok = tokens[0];
      if (tok.type === 'number') return Number(tok.value);
      if (tok.type === 'string') return tok.value;
      if (tok.type === 'boolean') return tok.value === 'true';
      if (tok.type === 'ref') {
        if (this.isRange(tok.value)) return this.expandRange(tok.value);
        return this.evaluateCellRef(tok.value);
      }
      return tok.value;
    }

    const output: Array<{ type: string; value: any }> = [];
    const opStack: Array<{ type: string; value: string }> = [];

    for (let i = 0; i < tokens.length; i++) {
      const tok = tokens[i];

      if (tok.type === 'number') {
        output.push({ type: 'number', value: Number(tok.value) });
      } else if (tok.type === 'string') {
        output.push({ type: 'string', value: tok.value });
      } else if (tok.type === 'boolean') {
        output.push({ type: 'boolean', value: tok.value === 'true' });
      } else if (tok.type === 'ref') {
        if (i + 2 < tokens.length && tokens[i + 1].type === 'colon' && tokens[i + 2].type === 'ref') {
          const rangeStr = `${tok.value}:${tokens[i + 2].value}`;
          output.push({ type: 'range', value: this.expandRange(rangeStr) });
          i += 2;
        } else {
          const val = this.evaluateCellRef(tok.value);
          output.push({ type: 'value', value: val });
        }
      } else if (tok.type === 'postfix_percent') {
        // Postfix operator: evaluate immediately since it's highest precedence
        output.push({ type: 'postfix_percent', value: '%' });
      } else if (tok.type === 'unary') {
        opStack.push({ type: 'unary', value: tok.value });
      } else if (tok.type === 'operator') {
        const prec = FormulaEngine.PRECEDENCE[tok.value] ?? -1;
        while (opStack.length > 0) {
          const top = opStack[opStack.length - 1];
          if (top.type === 'lparen') break;
          if (top.type === 'unary') {
            output.push(opStack.pop()!);
            continue;
          }
          const topPrec = FormulaEngine.PRECEDENCE[top.value] ?? -1;
          if (topPrec > prec || (topPrec === prec && !FormulaEngine.RIGHT_ASSOC.has(tok.value))) {
            output.push(opStack.pop()!);
          } else {
            break;
          }
        }
        opStack.push(tok);
      } else if (tok.type === 'lparen') {
        opStack.push(tok);
      } else if (tok.type === 'rparen') {
        while (opStack.length > 0 && opStack[opStack.length - 1].type !== 'lparen') {
          output.push(opStack.pop()!);
        }
        if (opStack.length > 0) opStack.pop();
      }
    }

    while (opStack.length > 0) {
      output.push(opStack.pop()!);
    }

    const evalStack: any[] = [];

    for (const item of output) {
      if (item.type === 'postfix_percent') {
        const val = evalStack.pop();
        evalStack.push(Number(val) / 100);
      } else if (item.type === 'unary') {
        const val = evalStack.pop();
        if (item.value === '-') {
          evalStack.push(-Number(val));
        } else {
          evalStack.push(+Number(val));
        }
      } else if (item.type === 'operator') {
        const right = evalStack.pop();
        const left = evalStack.pop();
        evalStack.push(this.applyOperator(item.value, left, right));
      } else {
        evalStack.push(item.value);
      }
    }

    return evalStack.length > 0 ? evalStack[0] : '';
  }

  private applyOperator(op: string, left: any, right: any): any {
    const l = Number(left);
    const r = Number(right);

    switch (op) {
      case '+': return (isNaN(l) || isNaN(r)) ? String(left) + String(right) : l + r;
      case '-': return l - r;
      case '*': return l * r;
      case '/':
        if (r === 0) throw new Error('DIV/0');
        return l / r;
      case '^': return Math.pow(l, r);
      case '&': return String(left) + String(right);
      case '>': return l > r;
      case '<': return l < r;
      case '>=': return l >= r;
      case '<=': return l <= r;
      case '=': return left == right;
      case '<>': case '!=': return left != right;
      default: return 0;
    }
  }
  
  /**
   * Check if expression is a range (A1:B10)
   */
  private isRange(expr: string): boolean {
    return /^\$?[A-Z]+\$?[0-9]+:\$?[A-Z]+\$?[0-9]+$/.test(expr);
  }
  
  /**
   * Expand range to array of values
   * A1:A5 → [valueA1, valueA2, valueA3, valueA4, valueA5]
   */
  private expandRange(range: string): any[] {
    const [start, end] = range.split(':');
    const startPos = this.parseA1Notation(start);
    const endPos = this.parseA1Notation(end);
    
    const values: any[] = [];
    for (let r = startPos.row; r <= endPos.row; r++) {
      for (let c = startPos.col; c <= endPos.col; c++) {
        const cellRef = this.toA1Notation(r, c);
        const value = this.getCellValue(cellRef);
        values.push(value !== undefined ? value : '');
      }
    }
    
    return values;
  }

  /**
   * Expand range to 2D array of values
   * A1:C3 → [[valA1, valB1, valC1], [valA2, valB2, valC2], [valA3, valB3, valC3]]
   */
  private expandRange2D(range: string): { values: any[][]; rows: number; cols: number } {
    const [start, end] = range.split(':');
    const startPos = this.parseA1Notation(start);
    const endPos = this.parseA1Notation(end);
    
    const numRows = endPos.row - startPos.row + 1;
    const numCols = endPos.col - startPos.col + 1;
    const values: any[][] = [];
    
    for (let r = startPos.row; r <= endPos.row; r++) {
      const rowValues: any[] = [];
      for (let c = startPos.col; c <= endPos.col; c++) {
        const cellRef = this.toA1Notation(r, c);
        const value = this.getCellValue(cellRef);
        rowValues.push(value !== undefined ? value : '');
      }
      values.push(rowValues);
    }
    
    return { values, rows: numRows, cols: numCols };
  }
  
  /**
   * Parse A1 notation to row/col
   */
  private parseA1Notation(ref: string): { row: number; col: number } {
    const cleanRef = ref.replace(/\$/g, '');
    const match = cleanRef.match(/^([A-Z]+)([0-9]+)$/);
    if (!match) throw new Error(`Invalid cell reference: ${ref}`);
    
    const colStr = match[1];
    const rowStr = match[2];
    
    // Convert column letters to number (A=0, B=1, ..., Z=25, AA=26, etc.)
    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
    }
    col -= 1; // 0-indexed
    
    const row = parseInt(rowStr, 10) - 1; // 0-indexed
    
    return { row, col };
  }
  
  /**
   * Convert row/col to A1 notation
   */
  private toA1Notation(row: number, col: number): string {
    let colStr = '';
    let c = col + 1; // 1-indexed for conversion
    
    while (c > 0) {
      const remainder = (c - 1) % 26;
      colStr = String.fromCharCode(65 + remainder) + colStr;
      c = Math.floor((c - 1) / 26);
    }
    
    return `${colStr}${row + 1}`;
  }

  /**
   * Evaluate function call
   */
  private evaluateFunction(expr: string): any {
    const fnMatch = expr.match(/^([A-Z]+)\((.*)\)$/i);
    if (!fnMatch) throw new Error('Invalid function');

    const fnName = fnMatch[1].toUpperCase();
    const argsStr = fnMatch[2];
    const args = this.parseArgs(argsStr);

    // Evaluate each argument
    const evaluatedArgs = args.map(arg => this.evaluateExpression(arg));

    // Execute function
    switch (fnName) {
      // ========== Math/Aggregation Functions ==========
      case 'SUM':
        return this.flattenArgs(evaluatedArgs)
          .map(v => Number(v))
          .filter(v => !isNaN(v))
          .reduce((sum, val) => sum + val, 0);
      
      case 'AVERAGE':
      case 'AVG': {
        const numbers = this.flattenArgs(evaluatedArgs)
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        return numbers.length > 0 ? numbers.reduce((s, v) => s + v, 0) / numbers.length : 0;
      }
      
      case 'MIN':
        return Math.min(...this.flattenArgs(evaluatedArgs).map(v => Number(v)).filter(v => !isNaN(v)));
      
      case 'MAX':
        return Math.max(...this.flattenArgs(evaluatedArgs).map(v => Number(v)).filter(v => !isNaN(v)));
      
      case 'COUNT':
        return this.flattenArgs(evaluatedArgs).filter(v => v !== '' && v !== null && v !== undefined).length;
      
      case 'COUNTA':
        return this.flattenArgs(evaluatedArgs).filter(v => v !== '' && v !== null && v !== undefined).length;
      
      case 'COUNTIF': {
        const range = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        return this.countIf(range, criteria);
      }
      
      case 'SUMIF': {
        const criteriaRange = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        const sumRange = evaluatedArgs[2] ? this.flattenArgs([evaluatedArgs[2]]) : criteriaRange;
        return this.sumIf(criteriaRange, criteria, sumRange);
      }
      
      case 'AVERAGEIF': {
        const criteriaRange = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        const avgRange = evaluatedArgs[2] ? this.flattenArgs([evaluatedArgs[2]]) : criteriaRange;
        return this.averageIf(criteriaRange, criteria, avgRange);
      }
      
      // ========== Logical Functions ==========
      case 'IF':
        return evaluatedArgs[0] ? evaluatedArgs[1] : evaluatedArgs[2];
      
      case 'AND':
        return evaluatedArgs.every(arg => Boolean(arg));
      
      case 'OR':
        return evaluatedArgs.some(arg => Boolean(arg));
      
      case 'NOT':
        return !evaluatedArgs[0];
      
      case 'IFERROR': {
        try {
          const result = this.evaluateExpression(args[0]);
          if (result === null || result === undefined || 
              (typeof result === 'string' && result.startsWith('#'))) {
            return evaluatedArgs[1] !== undefined ? evaluatedArgs[1] : '';
          }
          return result;
        } catch {
          return evaluatedArgs[1] !== undefined ? evaluatedArgs[1] : '';
        }
      }
      
      case 'ISBLANK':
        return evaluatedArgs[0] === '' || evaluatedArgs[0] === null || evaluatedArgs[0] === undefined;
      
      case 'ISNUMBER':
        return !isNaN(Number(evaluatedArgs[0])) && evaluatedArgs[0] !== '';
      
      case 'ISTEXT':
        return typeof evaluatedArgs[0] === 'string' && evaluatedArgs[0] !== '';
      
      // ========== Math Functions ==========
      case 'ROUND':
        return Math.round(Number(evaluatedArgs[0]) * Math.pow(10, Number(evaluatedArgs[1] || 0))) / Math.pow(10, Number(evaluatedArgs[1] || 0));
      
      case 'ABS':
        return Math.abs(Number(evaluatedArgs[0]));
      
      case 'SQRT':
        return Math.sqrt(Number(evaluatedArgs[0]));
      
      case 'POW':
      case 'POWER':
        return Math.pow(Number(evaluatedArgs[0]), Number(evaluatedArgs[1]));
      
      // ========== Text Functions ==========
      case 'CONCAT':
      case 'CONCATENATE':
        return this.flattenArgs(evaluatedArgs).join('');
      
      case 'UPPER':
        return String(evaluatedArgs[0]).toUpperCase();
      
      case 'LOWER':
        return String(evaluatedArgs[0]).toLowerCase();
      
      case 'LEN':
      case 'LENGTH':
        return String(evaluatedArgs[0]).length;
      
      case 'LEFT':
        return String(evaluatedArgs[0]).substring(0, Number(evaluatedArgs[1] || 1));
      
      case 'RIGHT': {
        const text = String(evaluatedArgs[0]);
        const count = Number(evaluatedArgs[1] || 1);
        return text.substring(text.length - count);
      }
      
      case 'MID':
        return String(evaluatedArgs[0]).substring(
          Number(evaluatedArgs[1]) - 1,
          Number(evaluatedArgs[1]) - 1 + Number(evaluatedArgs[2])
        );
      
      case 'TRIM':
        return String(evaluatedArgs[0]).trim();
      
      case 'SUBSTITUTE':
        return String(evaluatedArgs[0]).replace(
          new RegExp(String(evaluatedArgs[1]).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
          String(evaluatedArgs[2])
        );
      
      // ========== Date/Time Functions ==========
      case 'TODAY':
        return new Date().toLocaleDateString('en-US');
      
      case 'NOW':
        return new Date().toLocaleString('en-US');
      
      case 'DATE': {
        const year = Number(evaluatedArgs[0]);
        const month = Number(evaluatedArgs[1]);
        const day = Number(evaluatedArgs[2]);
        return new Date(year, month - 1, day).toLocaleDateString('en-US');
      }
      
      case 'YEAR':
        return new Date(evaluatedArgs[0]).getFullYear();
      
      case 'MONTH':
        return new Date(evaluatedArgs[0]).getMonth() + 1;
      
      case 'DAY':
        return new Date(evaluatedArgs[0]).getDate();
      
      case 'WEEKDAY':
        return new Date(evaluatedArgs[0]).getDay() + 1; // 1=Sunday, 7=Saturday
      
      case 'DATEDIF': {
        const startDate = new Date(evaluatedArgs[0]);
        const endDate = new Date(evaluatedArgs[1]);
        const unit = String(evaluatedArgs[2]).toUpperCase();
        const diff = endDate.getTime() - startDate.getTime();
        
        switch (unit) {
          case 'D': return Math.floor(diff / (1000 * 60 * 60 * 24));
          case 'M': return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
          case 'Y': return endDate.getFullYear() - startDate.getFullYear();
          default: return 0;
        }
      }
      
      // ========== Lookup Functions ==========
      case 'VLOOKUP': {
        const lookupValue = evaluatedArgs[0];
        const tableRange = args[1].trim(); // Get raw range string before evaluation
        const colIndex = Number(evaluatedArgs[2]);
        const exactMatch = evaluatedArgs[3] === undefined ? true : !evaluatedArgs[3];
        
        // Get 2D table data from range
        let table2D: { values: any[][]; rows: number; cols: number };
        if (this.isRange(tableRange)) {
          table2D = this.expandRange2D(tableRange);
        } else {
          // Fallback: try to use the evaluated arg as flat array
          const flat = this.flattenArgs([evaluatedArgs[1]]);
          table2D = { values: [flat], rows: 1, cols: flat.length };
        }
        
        if (colIndex < 1 || colIndex > table2D.cols) {
          throw new Error('VALUE');
        }
        
        // Search first column for lookup value
        for (let r = 0; r < table2D.rows; r++) {
          const cellValue = table2D.values[r][0];
          
          if (exactMatch) {
            if (cellValue == lookupValue || String(cellValue) === String(lookupValue)) {
              return table2D.values[r][colIndex - 1];
            }
          } else {
            // Approximate match: find largest value <= lookupValue (data must be sorted)
            const numLookup = Number(lookupValue);
            const numCell = Number(cellValue);
            
            if (!isNaN(numLookup) && !isNaN(numCell)) {
              if (r === table2D.rows - 1 || Number(table2D.values[r + 1][0]) > numLookup) {
                if (numCell <= numLookup) {
                  return table2D.values[r][colIndex - 1];
                }
              }
            }
          }
        }
        
        return '#N/A';
      }

      case 'HLOOKUP': {
        const lookupValue = evaluatedArgs[0];
        const tableRange = args[1].trim();
        const rowIndex = Number(evaluatedArgs[2]);
        const exactMatch = evaluatedArgs[3] === undefined ? true : !evaluatedArgs[3];
        
        let table2D: { values: any[][]; rows: number; cols: number };
        if (this.isRange(tableRange)) {
          table2D = this.expandRange2D(tableRange);
        } else {
          const flat = this.flattenArgs([evaluatedArgs[1]]);
          table2D = { values: [flat], rows: 1, cols: flat.length };
        }
        
        if (rowIndex < 1 || rowIndex > table2D.rows) {
          throw new Error('VALUE');
        }
        
        // Search first row for lookup value
        for (let c = 0; c < table2D.cols; c++) {
          const cellValue = table2D.values[0][c];
          
          if (exactMatch) {
            if (cellValue == lookupValue || String(cellValue) === String(lookupValue)) {
              return table2D.values[rowIndex - 1][c];
            }
          } else {
            const numLookup = Number(lookupValue);
            const numCell = Number(cellValue);
            
            if (!isNaN(numLookup) && !isNaN(numCell)) {
              if (c === table2D.cols - 1 || Number(table2D.values[0][c + 1]) > numLookup) {
                if (numCell <= numLookup) {
                  return table2D.values[rowIndex - 1][c];
                }
              }
            }
          }
        }
        
        return '#N/A';
      }

      case 'INDEX': {
        const rangeArg = args[0].trim();
        const rowNum = Number(evaluatedArgs[1]);
        const colNum = evaluatedArgs[2] !== undefined ? Number(evaluatedArgs[2]) : 1;
        
        let table2D: { values: any[][]; rows: number; cols: number };
        if (this.isRange(rangeArg)) {
          table2D = this.expandRange2D(rangeArg);
        } else if (Array.isArray(evaluatedArgs[0])) {
          const flat = this.flattenArgs([evaluatedArgs[0]]);
          table2D = { values: [flat], rows: 1, cols: flat.length };
        } else {
          return evaluatedArgs[0]; // Single value
        }
        
        if (rowNum < 1 || rowNum > table2D.rows || colNum < 1 || colNum > table2D.cols) {
          return '#REF!';
        }
        
        return table2D.values[rowNum - 1][colNum - 1];
      }

      case 'MATCH': {
        const lookupValue = evaluatedArgs[0];
        const lookupArray = this.flattenArgs([evaluatedArgs[1]]);
        const matchType = evaluatedArgs[2] !== undefined ? Number(evaluatedArgs[2]) : 1;
        
        if (matchType === 0) {
          // Exact match
          for (let i = 0; i < lookupArray.length; i++) {
            if (lookupArray[i] == lookupValue || String(lookupArray[i]) === String(lookupValue)) {
              return i + 1; // 1-based position
            }
          }
          return '#N/A';
        } else if (matchType === 1) {
          // Largest value <= lookupValue (array must be ascending)
          let lastMatch = -1;
          for (let i = 0; i < lookupArray.length; i++) {
            const num = Number(lookupArray[i]);
            const target = Number(lookupValue);
            if (!isNaN(num) && !isNaN(target) && num <= target) {
              lastMatch = i;
            }
          }
          return lastMatch >= 0 ? lastMatch + 1 : '#N/A';
        } else {
          // matchType === -1: Smallest value >= lookupValue (array must be descending)
          for (let i = 0; i < lookupArray.length; i++) {
            const num = Number(lookupArray[i]);
            const target = Number(lookupValue);
            if (!isNaN(num) && !isNaN(target) && num >= target) {
              return i + 1;
            }
          }
          return '#N/A';
        }
      }
      
      default:
        throw new Error(`Unknown function: ${fnName}`);
    }
  }
  
  /**
   * Flatten nested arrays (for range arguments)
   */
  private flattenArgs(args: any[]): any[] {
    const result: any[] = [];
    for (const arg of args) {
      if (Array.isArray(arg)) {
        result.push(...this.flattenArgs(arg));
      } else {
        result.push(arg);
      }
    }
    return result;
  }
  
  /**
   * COUNTIF helper
   */
  private countIf(range: any[], criteria: string): number {
    const evaluator = this.parseCriteria(criteria);
    return range.filter(v => evaluator(v)).length;
  }
  
  /**
   * SUMIF helper
   */
  private sumIf(criteriaRange: any[], criteria: string, sumRange: any[]): number {
    const evaluator = this.parseCriteria(criteria);
    let sum = 0;
    
    for (let i = 0; i < criteriaRange.length; i++) {
      if (evaluator(criteriaRange[i])) {
        sum += Number(sumRange[i]) || 0;
      }
    }
    
    return sum;
  }
  
  /**
   * AVERAGEIF helper
   */
  private averageIf(criteriaRange: any[], criteria: string, avgRange: any[]): number {
    const evaluator = this.parseCriteria(criteria);
    let sum = 0;
    let count = 0;
    
    for (let i = 0; i < criteriaRange.length; i++) {
      if (evaluator(criteriaRange[i])) {
        sum += Number(avgRange[i]) || 0;
        count++;
      }
    }
    
    return count > 0 ? sum / count : 0;
  }
  
  /**
   * Parse criteria string (e.g., ">100", "=Apple", ">=50")
   */
  private parseCriteria(criteria: string): (value: any) => boolean {
    const match = criteria.match(/^(>=?|<=?|=|<>)(.+)$/);
    
    if (!match) {
      // Exact match
      return (v) => v == criteria;
    }
    
    const [, op, value] = match;
    const numValue = Number(value);
    
    switch (op) {
      case '>': return (v) => Number(v) > numValue;
      case '>=': return (v) => Number(v) >= numValue;
      case '<': return (v) => Number(v) < numValue;
      case '<=': return (v) => Number(v) <= numValue;
      case '=': return (v) => v == value;
      case '<>': return (v) => v != value;
      default: return () => false;
    }
  }
  
  /**
   * Parse function arguments
   */
  private parseArgs(argsStr: string): string[] {
    const args: string[] = [];
    let current = '';
    let depth = 0;
    let inString = false;
    let stringChar = '';

    for (let i = 0; i < argsStr.length; i++) {
      const char = argsStr[i];

      if ((char === '"' || char === "'") && !inString) {
        inString = true;
        stringChar = char;
        current += char;
      } else if (char === stringChar && inString) {
        inString = false;
        current += char;
      } else if (char === '(' && !inString) {
        depth++;
        current += char;
      } else if (char === ')' && !inString) {
        depth--;
        current += char;
      } else if (char === ',' && depth === 0 && !inString) {
        args.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    if (current.trim()) {
      args.push(current.trim());
    }

    return args;
  }

  /**
   * Evaluate cell reference — returns 0 for empty/missing cells (spreadsheet convention)
   */
  private evaluateCellRef(ref: string): any {
    const value = this.getCellValue(ref);
    if (value === undefined || value === null || value === '') return 0;
    return value;
  }

}

